import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { showInfoMessage } from 'ducks/layout';
import { useCore } from 'contexts/core';
import { useTranslations } from 'hooks/useTranslations';
import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { getPositionOfEntityTop, getTopPanelForFrame } from 'rangeConfig/besta/utils/topPanels';
import { MeasurementUtility } from '@inter-ikea-kompis/utilities';
import {
  shouldShowTopPanelHeighInfoMessage,
  showTopPanelHeighInfoMessage
} from './TopPanelHeightInfoFeature';
import { useServiceSettings } from 'contexts/serviceSettings';

/**
 * Shows a toast info message first time user drags a frame with top panel above 150cm
 */
const TopPanelHeightMessage = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();
  const shouldShowMessage = useSelector(shouldShowTopPanelHeighInfoMessage);

  useEffect(() => {
    const onGuestConnectionComponentUpdated = (entity: Entity) => {
      if (
        entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
        getTopPanelForFrame(entity, coreApi) !== null &&
        getPositionOfEntityTop(entity, coreApi) > 1500
      ) {
        dispatch(showTopPanelHeighInfoMessage());
      }
    };

    coreApi
      .getServices()
      .entityManager.subscribeAdd(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID, (entity: Entity) =>
        onGuestConnectionComponentUpdated(entity)
      );
    coreApi
      .getServices()
      .entityManager.subscribeUpdate(
        ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID,
        (entity: Entity) => onGuestConnectionComponentUpdated(entity)
      );

    return () => {
      coreApi
        .getServices()
        .entityManager.unsubscribeAdd(
          ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID,
          onGuestConnectionComponentUpdated
        );
      coreApi
        .getServices()
        .entityManager.unsubscribeUpdate(
          ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID,
          onGuestConnectionComponentUpdated
        );
    };
  }, [coreApi, dispatch, shouldShowMessage]);

  useEffect(() => {
    if (shouldShowMessage) {
      const heightLimit = 1500;
      const height = useMetric
        ? heightLimit / 10
        : MeasurementUtility.metricToImperial(translations.kompis, heightLimit);
      const unit = useMetric
        ? formatMessage({ id: 'storageone.cm' })
        : formatMessage({ id: 'storageone.inches' });
      dispatch(
        showInfoMessage({
          message: 'storageone.dragTopPanelHeighRestrictionToastMessage',
          params: { height, unit }
        })
      );
    }
  }, [dispatch, formatMessage, shouldShowMessage, translations, useMetric]);

  return <></>;
};

export default TopPanelHeightMessage;
